/**
 * AIM (Automatic Item Modification) summary dialog —
 * port of WinForms `frmDietech_AutoItemModification_Summary`.
 *
 * Renders the Original / Modified pairs the AIM engine
 * proposed in response to a patient's diet-order change.
 * Each pair is two DataGrid rows joined by a `groupKey`;
 * the user toggles a Selected checkbox per PAIR to accept
 * (default) or reject the proposed change. Apply posts the
 * rejected log-row PKeys back to the Resolve endpoint, which
 * rolls them back via the wrapper's ReverseMP/AdjUpdate. Cancel
 * prompts the user to confirm (per user direction) and then
 * **commits** all changes unchanged — matches WinForms
 * behavior of leaving the transaction in place when the
 * summary form is dismissed without action.
 *
 * Print queues report 109 (AIM Meal Pattern Changes) with the run's change
 * set and context, as the desktop summary's Print does
 * (mbtnPrint_ItemClick). It shows only when the run returned a change set.
 */
import { useMemo, useState } from 'react';
import './AimDietUpdateDialog.css';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActionsBar from '../../components/shared/DialogActionsBar';
import { SECONDARY_BUTTON_SX } from '../../components/shared/dialogActionsSx';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import AppGrid from '../../components/grid/AppGrid';
import type { AppGridColumn } from '../../components/grid/types';
import { useNotificationStore } from '../../stores/notificationStore';
import { useGlobalStore } from '../../stores/globalStore';
import { useQueueReport } from '../../api/reportApi';
import { useFacilityDate } from '../../hooks/useFacilityDate';
import { canPrintMpChanges, mpChangesReportRequest } from './mpChangesReport';
import {
  useAimDietResolve,
  type AimDietRunResponse,
  type AimProposalDto,
  type AimRowSnapshot,
} from '../../api/aimDietApi';

interface Props {
  open: boolean;
  /**
   * Pre-open spinner state. When `true` the dialog renders an
   * "Analyzing diet change…" placeholder instead of the grid;
   * the parent flips this to `false` once the AIM mutation
   * resolves and either lands a `result` (transition to grid)
   * or auto-closes the dialog (no proposals / error).
   */
  loading?: boolean;
  facilityId: number;
  patientId: number;
  result: AimDietRunResponse | null;
  /** The resident's name, for report 109 when the run's context has none. */
  residentFullName?: string;
  onClose: (didResolve: boolean) => void;
}

// Row identity for the grid: one row per (proposal × original|modified).
interface DisplayRow {
  rowKey: string;
  groupKey: number; // === proposal.pKey, shared by the Original + Modified pair
  isOriginal: boolean;
  kind: 'mealPattern' | 'adjustment';
  group: string;
  mealtimeLabel: string;
  menuCategory: string | null;
  isGlobalLineage: boolean;
  isInactive: boolean;
  snapshot: AimRowSnapshot;
  // Hoisted onto every row so cellClassName can draw its
  // diff-highlight without re-looking-up the proposal.
  changedColumns: Set<string>;
  proposal: AimProposalDto;
}

// First-letter labels per user direction — column is narrow
// and the diff highlight is more legible when the cell holds
// just "B" / "L" / "D" / "S" (and "L/D" for combined ranges).
const MEALTIME_LABELS: Record<number, string> = {
  0: 'B',
  1: 'L',
  2: 'D',
  3: 'S',
};

function mealtimeText(p: AimProposalDto): string {
  if (p.mealtime != null) return MEALTIME_LABELS[p.mealtime] ?? `Meal ${p.mealtime}`;
  if (p.mealtimeCommaString) {
    return p.mealtimeCommaString
      .split(',')
      .map((s) => MEALTIME_LABELS[Number(s)] ?? '')
      .filter(Boolean)
      .join(' / ');
  }
  return '';
}

export default function AimDietUpdateDialog({ open, loading, facilityId, patientId, result, residentFullName, onClose }: Props) {
  const resolve = useAimDietResolve();
  const userId = useGlobalStore((s) => s.userId);
  const queueReport = useQueueReport(facilityId);
  const { today: facilityToday } = useFacilityDate();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Default: every proposal accepted. The state stores the
  // groupKey (= proposal.pKey) of REJECTED proposals so the
  // common case (accept all) stays a single empty Set.
  const [rejected, setRejected] = useState<Set<number>>(new Set());

  const rows: DisplayRow[] = useMemo(() => {
    if (!result) return [];
    const out: DisplayRow[] = [];
    for (const p of result.proposals) {
      const meta = {
        groupKey: p.pKey,
        kind: p.kind,
        group: p.group ?? (p.kind === 'mealPattern' ? 'Meal Pattern' : 'Adjustment'),
        mealtimeLabel: mealtimeText(p),
        menuCategory: p.menuCategory,
        isGlobalLineage: p.isGlobalLineage,
        isInactive: p.isInactive,
        changedColumns: new Set(p.changedColumns),
        proposal: p,
      };
      out.push({ ...meta, rowKey: `${p.pKey}-O`, isOriginal: true, snapshot: p.original });
      out.push({ ...meta, rowKey: `${p.pKey}-M`, isOriginal: false, snapshot: p.modified });
    }
    return out;
  }, [result]);

  const toggleProposal = (groupKey: number, accept: boolean) => {
    setRejected((prev) => {
      const next = new Set(prev);
      if (accept) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const handleApply = async () => {
    if (!result) return;
    const rejectedProposals = result.proposals.filter((p) => rejected.has(p.pKey));
    const rejectedMPModified = rejectedProposals.filter((p) => p.kind === 'mealPattern').map((p) => p.pKey_Log_Prd_Meal_Pattern_Modified);
    const rejectedMPOriginal = rejectedProposals.filter((p) => p.kind === 'mealPattern').map((p) => p.pKey_Log_Prd_Meal_Pattern_Original);
    const rejectedAdjModified = rejectedProposals.filter((p) => p.kind === 'adjustment').map((p) => p.pKey_Log_Prd_Adjustment_Modified);
    const rejectedAdjOriginal = rejectedProposals.filter((p) => p.kind === 'adjustment').map((p) => p.pKey_Log_Prd_Adjustment_Original);
    try {
      await resolve.mutateAsync({
        facilityId,
        patientId,
        pKey_Log_Transaction: result.pKey_Log_Transaction,
        rejectedMPModified,
        rejectedMPOriginal,
        rejectedAdjModified,
        rejectedAdjOriginal,
      });
      // Plain words: "AIM" is not a name staff see anywhere else, and the
      // changes land on the resident's meal pattern (its adjustments included).
      const kept = result.proposals.length - rejected.size;
      addNotification(
        kept > 0
          ? `Meal pattern updated (${kept} ${kept === 1 ? 'change' : 'changes'})`
          : 'Meal pattern left as it was',
        'success',
      );
      setRejected(new Set());
      onClose(true);
    } catch (err) {
      addNotification(`Meal pattern not updated: ${(err as Error).message}`, 'error');
    }
  };

  // Cancel path — per user direction, prompt to confirm AND
  // make clear all changes will be saved. WinForms parity:
  // closing the form without OK leaves the transaction
  // applied (no rollback).
  const handleCancelRequest = () => setConfirmCancel(true);
  const handleCancelConfirmed = () => {
    setConfirmCancel(false);
    setRejected(new Set());
    onClose(false);
  };

  const columns = useMemo<AppGridColumn<DisplayRow>[]>(() => [
    {
      dataField: 'group',
      caption: 'Group',
      groupIndex: 0,
      groupCellRender: ({ value }) => (
        <span style={{ fontWeight: 600, fontSize: 'var(--font-size-base)' }}>
          {String(value ?? '')}
        </span>
      ),
    },
    {
      name: 'accept',
      caption: 'Accept',
      width: 95,
      allowSorting: false,
      alignment: 'center',
      cellRender: ({ data: row }) => {
        // Checkbox rides on the NEW row (Modified), matching the WinForms
        // summary form where the Selected checkbox lives on the "after"
        // pair entry.
        if (row.isOriginal) return null;
        const accepted = !rejected.has(row.groupKey);
        return (
          <Checkbox
            size="small"
            checked={accepted}
            onChange={(e) => toggleProposal(row.groupKey, e.target.checked)}
            slotProps={{ input: { 'aria-label': 'Accept' } }}
            // No padding, so the checkbox does not make the New row taller
            // than the plain-text Old row.
            sx={{ p: 0 }}
          />
        );
      },
    },
    {
      name: 'oldNew',
      caption: '',
      width: 70,
      allowSorting: false,
      allowFiltering: false,
      allowResizing: false,
      cellRender: ({ data: row }) => (
        <span style={{ fontWeight: 600, color: row.isOriginal ? '#92400e' : '#0d6efd' }}>
          {row.isOriginal ? 'Old' : 'New'}
        </span>
      ),
    },
    { name: 'mealtime', caption: 'Mealtime', width: 140, calculateCellValue: (row) => row.mealtimeLabel },
    { name: 'menuCategory', caption: 'Menu Category', width: 140, calculateCellValue: (row) => row.menuCategory ?? '' },
    // These column names are the changedColumns keys the AIM engine reports.
    { name: 'Item', caption: 'Item', width: 140, calculateCellValue: (row) => row.snapshot.item ?? '' },
    { name: 'ItemSize', caption: 'Size', width: 80, alignment: 'right', calculateCellValue: (row) => row.snapshot.itemSize ?? '' },
    { name: 'SzMeasure', caption: 'Sz Measure', width: 100, calculateCellValue: (row) => row.snapshot.szMeasure ?? '' },
    { name: 'Consist', caption: 'Consist', width: 120, calculateCellValue: (row) => row.snapshot.consist ?? '' },
    { name: 'Portion', caption: 'Portion', width: 110, calculateCellValue: (row) => row.snapshot.portion ?? '' },
    { name: 'ServiceItem', caption: 'Service Item', width: 140, calculateCellValue: (row) => row.snapshot.serviceItem ?? '' },
    { name: 'ItemType', caption: 'Item Type', width: 120, calculateCellValue: (row) => row.snapshot.itemType ?? '' },
  ], [rejected]);

  // The dialog renders in three states:
  //   1. `!open`               → null (closed).
  //   2. `open && loading`     → spinner placeholder (the
  //                              AIM mutation is in flight).
  //   3. `open && result`      → proposals grid (rendered
  //                              below). Empty proposals
  //                              never reach the dialog —
  //                              the parent closes it via
  //                              the no-op branch before
  //                              mounting.
  if (!open) return null;
  const isLoading = loading || result == null;

  return (
    <>
      <Dialog open={open} onClose={isLoading ? () => { /* swallow — can't dismiss mid-fetch */ } : handleCancelRequest} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <span style={{ flex: 1 }}>
            {isLoading ? 'Analyzing diet change…' : 'Auto Item Modifications · Review & Apply'}
          </span>
          {!isLoading && result && (
            <Chip
              size="small"
              color={rejected.size === 0 ? 'success' : 'warning'}
              label={`Accepting ${result.proposals.length - rejected.size} / ${result.proposals.length}`}
            />
          )}
          {/* X close button (project-standard upper-right) —
              routes through `handleCancelRequest` so the
              "all changes will be saved" confirm prompt fires
              same as the Cancel button. Hidden while loading
              since there's nothing to cancel yet. */}
          {!isLoading && (
            <IconButton size="small" onClick={handleCancelRequest} aria-label="Close" sx={{ ml: 0.5 }}>
              <i className="bi bi-x-lg" style={{ fontSize: 'var(--font-size-base)' }} />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {isLoading ? (
            <div
              style={{
                height: 480,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                color: 'var(--text-secondary)',
              }}
            >
              <CircularProgress size={48} />
              <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 500 }}>
                Analyzing diet change…
              </div>
              <div style={{ fontSize: 'var(--font-size-small)', textAlign: 'center', maxWidth: 420 }}>
                The auto item modification rules are evaluating every meal pattern and
                adjustment against the new diet order. This usually takes a few seconds.
              </div>
            </div>
          ) : (
          <div style={{ height: 480 }}>
            <AppGrid<DisplayRow>
              ariaLabel="Auto Item Modifications"
              className="aim-grid"
              dataSource={rows}
              keyExpr="rowKey"
              columns={columns}
              height="100%"
              // No wrapping: longer Adjustment Item names would wrap to two
              // lines while shorter Meal Pattern names stayed on one, giving
              // uneven Old/New pair heights. Cut-off text shows in full in
              // the cell's tooltip.
              wordWrapEnabled={false}
              rowAlternationEnabled={false}
              sorting={{ mode: 'none' }}
              headerFilter={{ visible: false }}
              // Both the Meal Pattern and Adjustment groups stay open so
              // every pair shows without expanding.
              grouping={{ autoExpandAll: true }}
              // Strike through rows whose production row is already
              // Inactive (WinForms IsInactive grid styling).
              rowClassName={(row) => (row.isInactive && row.isOriginal ? 'aim-row-inactive' : undefined)}
              // Diff highlight: on the New row, only for the columns that
              // changed (the WinForms yellow cell tint).
              cellClassName={({ data, column }) => (
                !data.isOriginal && column.name != null && data.changedColumns.has(column.name) ? 'aim-cell-changed' : undefined
              )}
            />
          </div>
          )}
        </DialogContent>

        {/* Both Apply and Cancel wait while loading: there's nothing to
            apply or cancel until the AIM mutation resolves. The bar has no
            separate Cancel-disabled switch, so loading counts as busy
            (Apply keeps its label while loading). */}
        <DialogActionsBar
          onCancel={handleCancelRequest}
          onSave={() => void handleApply()}
          extra={result && canPrintMpChanges(result) ? (
            // Report 109 with this run's change set and context. It prints what AIM changed;
            // the Accept checkboxes decide what Apply keeps, as on the desktop.
            <Button
              variant="outlined"
              onClick={() => queueReport.mutate(mpChangesReportRequest(result, {
                facilityId,
                patientId,
                userId,
                dateFor: facilityToday(),
                residentFullName,
              }))}
              disabled={isLoading || queueReport.isPending || resolve.isPending}
              startIcon={<i className="bi bi-printer" aria-hidden="true" />}
              sx={SECONDARY_BUTTON_SX}
            >
              Print
            </Button>
          ) : undefined}
          saveLabel="Apply"
          savingLabel={resolve.isPending ? 'Applying…' : 'Apply'}
          saving={isLoading || resolve.isPending}
        />
      </Dialog>

      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Cancel</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 'var(--font-size-base)' }}>
            The AIM engine has <strong>already applied</strong> these proposed changes
            to the database. Closing this dialog WITHOUT Apply leaves them in place
            (matches the WinForms behavior: Cancel does not roll back).
            <br /><br />
            Continue closing?
          </div>
        </DialogContent>
        <DialogActionsBar
          onCancel={() => setConfirmCancel(false)}
          onSave={handleCancelConfirmed}
          saveLabel="Yes, close"
          saveColor="error"
          keyboard={false}
        />
      </Dialog>
    </>
  );
}
